import { useEffect, useRef, useState } from "react";

// Counts a KPI figure up to its value in a fixed time, however large the number. The old per-page
// counters stepped by 1 at most every 20ms, so 3,000 devices took a minute to land. This one eases
// over `duration` ms, redraws once per frame, carries on from the current figure when the value
// changes, and shows the figure at once when the user prefers reduced motion.
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const AnimatedCounter = ({ value, duration = 800 }) => {
  const end = parseInt(value) || 0;
  const [count, setCount] = useState(() => (prefersReducedMotion() ? end : 0));
  const countRef = useRef(count);

  useEffect(() => {
    const start = countRef.current;
    if (start === end) return;
    if (prefersReducedMotion() || duration <= 0) {
      countRef.current = end;
      setCount(end);
      return;
    }

    let frame;
    const began = performance.now();
    const step = (now) => {
      const t = Math.min((now - began) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(start + (end - start) * eased);
      countRef.current = next;
      setCount(next);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [end, duration]);

  return <span>{count}</span>;
};

export default AnimatedCounter;
